import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCompare } from "../context/CompareContext";
import { LogOut, Search, ClipboardList, ShieldCheck, GitCompare, GitPullRequest, HeartPulse } from "lucide-react";

const NAV = {
  latp_admin: [
    { to: "/admin", label: "Admin", Icon: ShieldCheck },
    { to: "/search", label: "Directory", Icon: Search },
    { to: "/report", label: "Reporting", Icon: ClipboardList },
  ],
  provider_admin: [
    { to: "/report", label: "Reporting", Icon: ClipboardList },
    { to: "/proposals", label: "Proposals", Icon: GitPullRequest },
    { to: "/search", label: "Directory", Icon: Search },
  ],
  provider_reporter: [{ to: "/report", label: "Reporting", Icon: ClipboardList }],
  referral_partner: [{ to: "/search", label: "Directory", Icon: Search }],
  auditor: [
    { to: "/admin", label: "Oversight", Icon: ShieldCheck },
    { to: "/search", label: "Directory", Icon: Search },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { ids } = useCompare();
  const loc = useLocation();
  const nav = useNavigate();
  const links = NAV[user?.role] || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-6 px-4 sm:px-6">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
            <img src="/logo.jpg" alt="CensusPulse" className="h-9 w-9 rounded-lg object-cover shadow-sm" />
            <span className="font-display text-lg font-extrabold tracking-tight text-slate-900">
              Census<span className="text-teal-700">Pulse</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            {links.map((l) => {
              const active = loc.pathname.startsWith(l.to);
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                    active ? "bg-teal-50 text-teal-800" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <l.Icon size={16} /> {l.label}
                </Link>
              );
            })}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            {(user?.role === "referral_partner" || user?.role === "auditor" || user?.role === "latp_admin") && (
              <Link
                to="/compare"
                data-testid="nav-compare"
                className="relative flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <GitCompare size={16} /> Compare
                {ids.length > 0 && (
                  <span className="grid h-5 min-w-5 place-items-center rounded-full bg-[#F07E1E] px-1 text-xs font-bold text-white">
                    {ids.length}
                  </span>
                )}
              </Link>
            )}
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
              <div className="text-xs capitalize text-slate-500">{user?.role?.replace(/_/g, " ")}</div>
            </div>
            <button
              onClick={async () => {
                await logout();
                nav("/login");
              }}
              data-testid="logout-btn"
              className="flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              <LogOut size={16} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6">{children}</main>
    </div>
  );
}
